/**
 * Countries data — ISO 3166-1 alpha-2
 *
 * Fields:
 *   code      — ISO 3166-1 alpha-2 (e.g. "ES")
 *   nameEn    — English name (used as i18n fallback)
 *   dialCode  — International dial prefix (e.g. "+34")
 *   flag      — Flag emoji derived from code
 *
 * i18n key pattern: `country.{{code}}` (e.g. `country.ES`)
 */

export interface Country {
  code: string;
  nameEn: string;
  dialCode: string;
  flag: string;
}

/** Generate flag emoji from ISO 3166-1 alpha-2 code */
export function flagEmoji(code: string): string {
  return code
    .toUpperCase()
    .split('')
    .map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
    .join('');
}

export const COUNTRIES: Country[] = [
  // ── Europe ──────────────────────────────────────────────────────────────────
  { code: 'AD', nameEn: 'Andorra',              dialCode: '+376',  flag: flagEmoji('AD') },
  { code: 'AL', nameEn: 'Albania',              dialCode: '+355',  flag: flagEmoji('AL') },
  { code: 'AT', nameEn: 'Austria',              dialCode: '+43',   flag: flagEmoji('AT') },
  { code: 'AX', nameEn: 'Åland Islands',        dialCode: '+358',  flag: flagEmoji('AX') },
  { code: 'BA', nameEn: 'Bosnia and Herzegovina', dialCode: '+387', flag: flagEmoji('BA') },
  { code: 'BE', nameEn: 'Belgium',              dialCode: '+32',   flag: flagEmoji('BE') },
  { code: 'BG', nameEn: 'Bulgaria',             dialCode: '+359',  flag: flagEmoji('BG') },
  { code: 'BY', nameEn: 'Belarus',              dialCode: '+375',  flag: flagEmoji('BY') },
  { code: 'CH', nameEn: 'Switzerland',          dialCode: '+41',   flag: flagEmoji('CH') },
  { code: 'CY', nameEn: 'Cyprus',               dialCode: '+357',  flag: flagEmoji('CY') },
  { code: 'CZ', nameEn: 'Czech Republic',       dialCode: '+420',  flag: flagEmoji('CZ') },
  { code: 'DE', nameEn: 'Germany',              dialCode: '+49',   flag: flagEmoji('DE') },
  { code: 'DK', nameEn: 'Denmark',              dialCode: '+45',   flag: flagEmoji('DK') },
  { code: 'EE', nameEn: 'Estonia',              dialCode: '+372',  flag: flagEmoji('EE') },
  { code: 'ES', nameEn: 'Spain',                dialCode: '+34',   flag: flagEmoji('ES') },
  { code: 'FI', nameEn: 'Finland',              dialCode: '+358',  flag: flagEmoji('FI') },
  { code: 'FO', nameEn: 'Faroe Islands',        dialCode: '+298',  flag: flagEmoji('FO') },
  { code: 'FR', nameEn: 'France',               dialCode: '+33',   flag: flagEmoji('FR') },
  { code: 'GB', nameEn: 'United Kingdom',       dialCode: '+44',   flag: flagEmoji('GB') },
  { code: 'GG', nameEn: 'Guernsey',             dialCode: '+44',   flag: flagEmoji('GG') },
  { code: 'GI', nameEn: 'Gibraltar',            dialCode: '+350',  flag: flagEmoji('GI') },
  { code: 'GR', nameEn: 'Greece',               dialCode: '+30',   flag: flagEmoji('GR') },
  { code: 'HR', nameEn: 'Croatia',              dialCode: '+385',  flag: flagEmoji('HR') },
  { code: 'HU', nameEn: 'Hungary',              dialCode: '+36',   flag: flagEmoji('HU') },
  { code: 'IE', nameEn: 'Ireland',              dialCode: '+353',  flag: flagEmoji('IE') },
  { code: 'IM', nameEn: 'Isle of Man',          dialCode: '+44',   flag: flagEmoji('IM') },
  { code: 'IS', nameEn: 'Iceland',              dialCode: '+354',  flag: flagEmoji('IS') },
  { code: 'IT', nameEn: 'Italy',                dialCode: '+39',   flag: flagEmoji('IT') },
  { code: 'JE', nameEn: 'Jersey',               dialCode: '+44',   flag: flagEmoji('JE') },
  { code: 'LI', nameEn: 'Liechtenstein',        dialCode: '+423',  flag: flagEmoji('LI') },
  { code: 'LT', nameEn: 'Lithuania',            dialCode: '+370',  flag: flagEmoji('LT') },
  { code: 'LU', nameEn: 'Luxembourg',           dialCode: '+352',  flag: flagEmoji('LU') },
  { code: 'LV', nameEn: 'Latvia',               dialCode: '+371',  flag: flagEmoji('LV') },
  { code: 'MC', nameEn: 'Monaco',               dialCode: '+377',  flag: flagEmoji('MC') },
  { code: 'MD', nameEn: 'Moldova',              dialCode: '+373',  flag: flagEmoji('MD') },
  { code: 'ME', nameEn: 'Montenegro',           dialCode: '+382',  flag: flagEmoji('ME') },
  { code: 'MK', nameEn: 'North Macedonia',      dialCode: '+389',  flag: flagEmoji('MK') },
  { code: 'MT', nameEn: 'Malta',                dialCode: '+356',  flag: flagEmoji('MT') },
  { code: 'NL', nameEn: 'Netherlands',          dialCode: '+31',   flag: flagEmoji('NL') },
  { code: 'NO', nameEn: 'Norway',               dialCode: '+47',   flag: flagEmoji('NO') },
  { code: 'PL', nameEn: 'Poland',               dialCode: '+48',   flag: flagEmoji('PL') },
  { code: 'PT', nameEn: 'Portugal',             dialCode: '+351',  flag: flagEmoji('PT') },
  { code: 'RO', nameEn: 'Romania',              dialCode: '+40',   flag: flagEmoji('RO') },
  { code: 'RS', nameEn: 'Serbia',               dialCode: '+381',  flag: flagEmoji('RS') },
  { code: 'RU', nameEn: 'Russia',               dialCode: '+7',    flag: flagEmoji('RU') },
  { code: 'SE', nameEn: 'Sweden',               dialCode: '+46',   flag: flagEmoji('SE') },
  { code: 'SI', nameEn: 'Slovenia',             dialCode: '+386',  flag: flagEmoji('SI') },
  { code: 'SK', nameEn: 'Slovakia',             dialCode: '+421',  flag: flagEmoji('SK') },
  { code: 'SM', nameEn: 'San Marino',           dialCode: '+378',  flag: flagEmoji('SM') },
  { code: 'TR', nameEn: 'Turkey',               dialCode: '+90',   flag: flagEmoji('TR') },
  { code: 'UA', nameEn: 'Ukraine',              dialCode: '+380',  flag: flagEmoji('UA') },
  { code: 'VA', nameEn: 'Vatican City',         dialCode: '+379',  flag: flagEmoji('VA') },
  { code: 'XK', nameEn: 'Kosovo',               dialCode: '+383',  flag: flagEmoji('XK') },

  // ── Americas ─────────────────────────────────────────────────────────────────
  { code: 'AG', nameEn: 'Antigua and Barbuda',  dialCode: '+1268', flag: flagEmoji('AG') },
  { code: 'AI', nameEn: 'Anguilla',             dialCode: '+1264', flag: flagEmoji('AI') },
  { code: 'AR', nameEn: 'Argentina',            dialCode: '+54',   flag: flagEmoji('AR') },
  { code: 'AW', nameEn: 'Aruba',                dialCode: '+297',  flag: flagEmoji('AW') },
  { code: 'BB', nameEn: 'Barbados',             dialCode: '+1246', flag: flagEmoji('BB') },
  { code: 'BL', nameEn: 'Saint Barthélemy',     dialCode: '+590',  flag: flagEmoji('BL') },
  { code: 'BM', nameEn: 'Bermuda',              dialCode: '+1441', flag: flagEmoji('BM') },
  { code: 'BO', nameEn: 'Bolivia',              dialCode: '+591',  flag: flagEmoji('BO') },
  { code: 'BQ', nameEn: 'Caribbean Netherlands', dialCode: '+599', flag: flagEmoji('BQ') },
  { code: 'BR', nameEn: 'Brazil',               dialCode: '+55',   flag: flagEmoji('BR') },
  { code: 'BS', nameEn: 'Bahamas',              dialCode: '+1242', flag: flagEmoji('BS') },
  { code: 'BZ', nameEn: 'Belize',               dialCode: '+501',  flag: flagEmoji('BZ') },
  { code: 'CA', nameEn: 'Canada',               dialCode: '+1',    flag: flagEmoji('CA') },
  { code: 'CL', nameEn: 'Chile',                dialCode: '+56',   flag: flagEmoji('CL') },
  { code: 'CO', nameEn: 'Colombia',             dialCode: '+57',   flag: flagEmoji('CO') },
  { code: 'CR', nameEn: 'Costa Rica',           dialCode: '+506',  flag: flagEmoji('CR') },
  { code: 'CU', nameEn: 'Cuba',                 dialCode: '+53',   flag: flagEmoji('CU') },
  { code: 'CW', nameEn: 'Curaçao',              dialCode: '+599',  flag: flagEmoji('CW') },
  { code: 'DM', nameEn: 'Dominica',             dialCode: '+1767', flag: flagEmoji('DM') },
  { code: 'DO', nameEn: 'Dominican Republic',   dialCode: '+1809', flag: flagEmoji('DO') },
  { code: 'EC', nameEn: 'Ecuador',              dialCode: '+593',  flag: flagEmoji('EC') },
  { code: 'FK', nameEn: 'Falkland Islands',     dialCode: '+500',  flag: flagEmoji('FK') },
  { code: 'GD', nameEn: 'Grenada',              dialCode: '+1473', flag: flagEmoji('GD') },
  { code: 'GF', nameEn: 'French Guiana',        dialCode: '+594',  flag: flagEmoji('GF') },
  { code: 'GL', nameEn: 'Greenland',            dialCode: '+299',  flag: flagEmoji('GL') },
  { code: 'GP', nameEn: 'Guadeloupe',           dialCode: '+590',  flag: flagEmoji('GP') },
  { code: 'GT', nameEn: 'Guatemala',            dialCode: '+502',  flag: flagEmoji('GT') },
  { code: 'GY', nameEn: 'Guyana',               dialCode: '+592',  flag: flagEmoji('GY') },
  { code: 'HN', nameEn: 'Honduras',             dialCode: '+504',  flag: flagEmoji('HN') },
  { code: 'HT', nameEn: 'Haiti',                dialCode: '+509',  flag: flagEmoji('HT') },
  { code: 'JM', nameEn: 'Jamaica',              dialCode: '+1876', flag: flagEmoji('JM') },
  { code: 'KN', nameEn: 'Saint Kitts and Nevis', dialCode: '+1869', flag: flagEmoji('KN') },
  { code: 'KY', nameEn: 'Cayman Islands',       dialCode: '+1345', flag: flagEmoji('KY') },
  { code: 'LC', nameEn: 'Saint Lucia',          dialCode: '+1758', flag: flagEmoji('LC') },
  { code: 'MF', nameEn: 'Saint Martin',         dialCode: '+590',  flag: flagEmoji('MF') },
  { code: 'MQ', nameEn: 'Martinique',           dialCode: '+596',  flag: flagEmoji('MQ') },
  { code: 'MS', nameEn: 'Montserrat',           dialCode: '+1664', flag: flagEmoji('MS') },
  { code: 'MX', nameEn: 'Mexico',               dialCode: '+52',   flag: flagEmoji('MX') },
  { code: 'NI', nameEn: 'Nicaragua',            dialCode: '+505',  flag: flagEmoji('NI') },
  { code: 'PA', nameEn: 'Panama',               dialCode: '+507',  flag: flagEmoji('PA') },
  { code: 'PE', nameEn: 'Peru',                 dialCode: '+51',   flag: flagEmoji('PE') },
  { code: 'PM', nameEn: 'Saint Pierre and Miquelon', dialCode: '+508', flag: flagEmoji('PM') },
  { code: 'PR', nameEn: 'Puerto Rico',          dialCode: '+1787', flag: flagEmoji('PR') },
  { code: 'PY', nameEn: 'Paraguay',             dialCode: '+595',  flag: flagEmoji('PY') },
  { code: 'SR', nameEn: 'Suriname',             dialCode: '+597',  flag: flagEmoji('SR') },
  { code: 'SV', nameEn: 'El Salvador',          dialCode: '+503',  flag: flagEmoji('SV') },
  { code: 'SX', nameEn: 'Sint Maarten',         dialCode: '+1721', flag: flagEmoji('SX') },
  { code: 'TC', nameEn: 'Turks and Caicos Islands', dialCode: '+1649', flag: flagEmoji('TC') },
  { code: 'TT', nameEn: 'Trinidad and Tobago',  dialCode: '+1868', flag: flagEmoji('TT') },
  { code: 'US', nameEn: 'United States',        dialCode: '+1',    flag: flagEmoji('US') },
  { code: 'UY', nameEn: 'Uruguay',              dialCode: '+598',  flag: flagEmoji('UY') },
  { code: 'VC', nameEn: 'Saint Vincent and the Grenadines', dialCode: '+1784', flag: flagEmoji('VC') },
  { code: 'VE', nameEn: 'Venezuela',            dialCode: '+58',   flag: flagEmoji('VE') },
  { code: 'VG', nameEn: 'British Virgin Islands', dialCode: '+1284', flag: flagEmoji('VG') },
  { code: 'VI', nameEn: 'U.S. Virgin Islands',  dialCode: '+1340', flag: flagEmoji('VI') },

  // ── Africa ───────────────────────────────────────────────────────────────────
  { code: 'AO', nameEn: 'Angola',               dialCode: '+244',  flag: flagEmoji('AO') },
  { code: 'BF', nameEn: 'Burkina Faso',         dialCode: '+226',  flag: flagEmoji('BF') },
  { code: 'BI', nameEn: 'Burundi',              dialCode: '+257',  flag: flagEmoji('BI') },
  { code: 'BJ', nameEn: 'Benin',                dialCode: '+229',  flag: flagEmoji('BJ') },
  { code: 'BW', nameEn: 'Botswana',             dialCode: '+267',  flag: flagEmoji('BW') },
  { code: 'CD', nameEn: 'DR Congo',             dialCode: '+243',  flag: flagEmoji('CD') },
  { code: 'CF', nameEn: 'Central African Republic', dialCode: '+236', flag: flagEmoji('CF') },
  { code: 'CG', nameEn: 'Congo',                dialCode: '+242',  flag: flagEmoji('CG') },
  { code: 'CI', nameEn: "Côte d'Ivoire",        dialCode: '+225',  flag: flagEmoji('CI') },
  { code: 'CM', nameEn: 'Cameroon',             dialCode: '+237',  flag: flagEmoji('CM') },
  { code: 'CV', nameEn: 'Cape Verde',           dialCode: '+238',  flag: flagEmoji('CV') },
  { code: 'DJ', nameEn: 'Djibouti',             dialCode: '+253',  flag: flagEmoji('DJ') },
  { code: 'DZ', nameEn: 'Algeria',              dialCode: '+213',  flag: flagEmoji('DZ') },
  { code: 'EG', nameEn: 'Egypt',                dialCode: '+20',   flag: flagEmoji('EG') },
  { code: 'EH', nameEn: 'Western Sahara',       dialCode: '+212',  flag: flagEmoji('EH') },
  { code: 'ER', nameEn: 'Eritrea',              dialCode: '+291',  flag: flagEmoji('ER') },
  { code: 'ET', nameEn: 'Ethiopia',             dialCode: '+251',  flag: flagEmoji('ET') },
  { code: 'GA', nameEn: 'Gabon',                dialCode: '+241',  flag: flagEmoji('GA') },
  { code: 'GH', nameEn: 'Ghana',                dialCode: '+233',  flag: flagEmoji('GH') },
  { code: 'GM', nameEn: 'Gambia',               dialCode: '+220',  flag: flagEmoji('GM') },
  { code: 'GN', nameEn: 'Guinea',               dialCode: '+224',  flag: flagEmoji('GN') },
  { code: 'GQ', nameEn: 'Equatorial Guinea',    dialCode: '+240',  flag: flagEmoji('GQ') },
  { code: 'GW', nameEn: 'Guinea-Bissau',        dialCode: '+245',  flag: flagEmoji('GW') },
  { code: 'KE', nameEn: 'Kenya',                dialCode: '+254',  flag: flagEmoji('KE') },
  { code: 'KM', nameEn: 'Comoros',              dialCode: '+269',  flag: flagEmoji('KM') },
  { code: 'LR', nameEn: 'Liberia',              dialCode: '+231',  flag: flagEmoji('LR') },
  { code: 'LS', nameEn: 'Lesotho',              dialCode: '+266',  flag: flagEmoji('LS') },
  { code: 'LY', nameEn: 'Libya',                dialCode: '+218',  flag: flagEmoji('LY') },
  { code: 'MA', nameEn: 'Morocco',              dialCode: '+212',  flag: flagEmoji('MA') },
  { code: 'MG', nameEn: 'Madagascar',           dialCode: '+261',  flag: flagEmoji('MG') },
  { code: 'ML', nameEn: 'Mali',                 dialCode: '+223',  flag: flagEmoji('ML') },
  { code: 'MR', nameEn: 'Mauritania',           dialCode: '+222',  flag: flagEmoji('MR') },
  { code: 'MU', nameEn: 'Mauritius',            dialCode: '+230',  flag: flagEmoji('MU') },
  { code: 'MW', nameEn: 'Malawi',               dialCode: '+265',  flag: flagEmoji('MW') },
  { code: 'MZ', nameEn: 'Mozambique',           dialCode: '+258',  flag: flagEmoji('MZ') },
  { code: 'NA', nameEn: 'Namibia',              dialCode: '+264',  flag: flagEmoji('NA') },
  { code: 'NE', nameEn: 'Niger',                dialCode: '+227',  flag: flagEmoji('NE') },
  { code: 'NG', nameEn: 'Nigeria',              dialCode: '+234',  flag: flagEmoji('NG') },
  { code: 'RE', nameEn: 'Réunion',              dialCode: '+262',  flag: flagEmoji('RE') },
  { code: 'RW', nameEn: 'Rwanda',               dialCode: '+250',  flag: flagEmoji('RW') },
  { code: 'SC', nameEn: 'Seychelles',           dialCode: '+248',  flag: flagEmoji('SC') },
  { code: 'SD', nameEn: 'Sudan',                dialCode: '+249',  flag: flagEmoji('SD') },
  { code: 'SH', nameEn: 'Saint Helena',         dialCode: '+290',  flag: flagEmoji('SH') },
  { code: 'SL', nameEn: 'Sierra Leone',         dialCode: '+232',  flag: flagEmoji('SL') },
  { code: 'SN', nameEn: 'Senegal',              dialCode: '+221',  flag: flagEmoji('SN') },
  { code: 'SO', nameEn: 'Somalia',              dialCode: '+252',  flag: flagEmoji('SO') },
  { code: 'SS', nameEn: 'South Sudan',          dialCode: '+211',  flag: flagEmoji('SS') },
  { code: 'ST', nameEn: 'São Tomé and Príncipe', dialCode: '+239', flag: flagEmoji('ST') },
  { code: 'SZ', nameEn: 'Eswatini',             dialCode: '+268',  flag: flagEmoji('SZ') },
  { code: 'TD', nameEn: 'Chad',                 dialCode: '+235',  flag: flagEmoji('TD') },
  { code: 'TG', nameEn: 'Togo',                 dialCode: '+228',  flag: flagEmoji('TG') },
  { code: 'TN', nameEn: 'Tunisia',              dialCode: '+216',  flag: flagEmoji('TN') },
  { code: 'TZ', nameEn: 'Tanzania',             dialCode: '+255',  flag: flagEmoji('TZ') },
  { code: 'UG', nameEn: 'Uganda',               dialCode: '+256',  flag: flagEmoji('UG') },
  { code: 'YT', nameEn: 'Mayotte',              dialCode: '+262',  flag: flagEmoji('YT') },
  { code: 'ZA', nameEn: 'South Africa',         dialCode: '+27',   flag: flagEmoji('ZA') },
  { code: 'ZM', nameEn: 'Zambia',               dialCode: '+260',  flag: flagEmoji('ZM') },
  { code: 'ZW', nameEn: 'Zimbabwe',             dialCode: '+263',  flag: flagEmoji('ZW') },

  // ── Middle East ──────────────────────────────────────────────────────────────
  { code: 'AE', nameEn: 'United Arab Emirates', dialCode: '+971',  flag: flagEmoji('AE') },
  { code: 'BH', nameEn: 'Bahrain',              dialCode: '+973',  flag: flagEmoji('BH') },
  { code: 'IL', nameEn: 'Israel',               dialCode: '+972',  flag: flagEmoji('IL') },
  { code: 'IQ', nameEn: 'Iraq',                 dialCode: '+964',  flag: flagEmoji('IQ') },
  { code: 'IR', nameEn: 'Iran',                 dialCode: '+98',   flag: flagEmoji('IR') },
  { code: 'JO', nameEn: 'Jordan',               dialCode: '+962',  flag: flagEmoji('JO') },
  { code: 'KW', nameEn: 'Kuwait',               dialCode: '+965',  flag: flagEmoji('KW') },
  { code: 'LB', nameEn: 'Lebanon',              dialCode: '+961',  flag: flagEmoji('LB') },
  { code: 'OM', nameEn: 'Oman',                 dialCode: '+968',  flag: flagEmoji('OM') },
  { code: 'PS', nameEn: 'Palestine',            dialCode: '+970',  flag: flagEmoji('PS') },
  { code: 'QA', nameEn: 'Qatar',                dialCode: '+974',  flag: flagEmoji('QA') },
  { code: 'SA', nameEn: 'Saudi Arabia',         dialCode: '+966',  flag: flagEmoji('SA') },
  { code: 'SY', nameEn: 'Syria',                dialCode: '+963',  flag: flagEmoji('SY') },
  { code: 'YE', nameEn: 'Yemen',                dialCode: '+967',  flag: flagEmoji('YE') },

  // ── Asia & Oceania ───────────────────────────────────────────────────────────
  { code: 'AF', nameEn: 'Afghanistan',          dialCode: '+93',   flag: flagEmoji('AF') },
  { code: 'AM', nameEn: 'Armenia',              dialCode: '+374',  flag: flagEmoji('AM') },
  { code: 'AS', nameEn: 'American Samoa',        dialCode: '+1684', flag: flagEmoji('AS') },
  { code: 'AU', nameEn: 'Australia',            dialCode: '+61',   flag: flagEmoji('AU') },
  { code: 'AZ', nameEn: 'Azerbaijan',           dialCode: '+994',  flag: flagEmoji('AZ') },
  { code: 'BD', nameEn: 'Bangladesh',           dialCode: '+880',  flag: flagEmoji('BD') },
  { code: 'BN', nameEn: 'Brunei',               dialCode: '+673',  flag: flagEmoji('BN') },
  { code: 'BT', nameEn: 'Bhutan',               dialCode: '+975',  flag: flagEmoji('BT') },
  { code: 'CK', nameEn: 'Cook Islands',         dialCode: '+682',  flag: flagEmoji('CK') },
  { code: 'CN', nameEn: 'China',                dialCode: '+86',   flag: flagEmoji('CN') },
  { code: 'FJ', nameEn: 'Fiji',                 dialCode: '+679',  flag: flagEmoji('FJ') },
  { code: 'FM', nameEn: 'Micronesia',           dialCode: '+691',  flag: flagEmoji('FM') },
  { code: 'GE', nameEn: 'Georgia',              dialCode: '+995',  flag: flagEmoji('GE') },
  { code: 'GU', nameEn: 'Guam',                 dialCode: '+1671', flag: flagEmoji('GU') },
  { code: 'HK', nameEn: 'Hong Kong',            dialCode: '+852',  flag: flagEmoji('HK') },
  { code: 'ID', nameEn: 'Indonesia',            dialCode: '+62',   flag: flagEmoji('ID') },
  { code: 'IN', nameEn: 'India',                dialCode: '+91',   flag: flagEmoji('IN') },
  { code: 'JP', nameEn: 'Japan',                dialCode: '+81',   flag: flagEmoji('JP') },
  { code: 'KG', nameEn: 'Kyrgyzstan',           dialCode: '+996',  flag: flagEmoji('KG') },
  { code: 'KH', nameEn: 'Cambodia',             dialCode: '+855',  flag: flagEmoji('KH') },
  { code: 'KI', nameEn: 'Kiribati',             dialCode: '+686',  flag: flagEmoji('KI') },
  { code: 'KP', nameEn: 'North Korea',          dialCode: '+850',  flag: flagEmoji('KP') },
  { code: 'KR', nameEn: 'South Korea',          dialCode: '+82',   flag: flagEmoji('KR') },
  { code: 'KZ', nameEn: 'Kazakhstan',           dialCode: '+7',    flag: flagEmoji('KZ') },
  { code: 'LA', nameEn: 'Laos',                 dialCode: '+856',  flag: flagEmoji('LA') },
  { code: 'LK', nameEn: 'Sri Lanka',            dialCode: '+94',   flag: flagEmoji('LK') },
  { code: 'MH', nameEn: 'Marshall Islands',     dialCode: '+692',  flag: flagEmoji('MH') },
  { code: 'MM', nameEn: 'Myanmar',              dialCode: '+95',   flag: flagEmoji('MM') },
  { code: 'MN', nameEn: 'Mongolia',             dialCode: '+976',  flag: flagEmoji('MN') },
  { code: 'MO', nameEn: 'Macau',                dialCode: '+853',  flag: flagEmoji('MO') },
  { code: 'MP', nameEn: 'Northern Mariana Islands', dialCode: '+1670', flag: flagEmoji('MP') },
  { code: 'MV', nameEn: 'Maldives',             dialCode: '+960',  flag: flagEmoji('MV') },
  { code: 'MY', nameEn: 'Malaysia',             dialCode: '+60',   flag: flagEmoji('MY') },
  { code: 'NC', nameEn: 'New Caledonia',        dialCode: '+687',  flag: flagEmoji('NC') },
  { code: 'NP', nameEn: 'Nepal',                dialCode: '+977',  flag: flagEmoji('NP') },
  { code: 'NR', nameEn: 'Nauru',                dialCode: '+674',  flag: flagEmoji('NR') },
  { code: 'NU', nameEn: 'Niue',                 dialCode: '+683',  flag: flagEmoji('NU') },
  { code: 'NZ', nameEn: 'New Zealand',          dialCode: '+64',   flag: flagEmoji('NZ') },
  { code: 'PF', nameEn: 'French Polynesia',     dialCode: '+689',  flag: flagEmoji('PF') },
  { code: 'PG', nameEn: 'Papua New Guinea',     dialCode: '+675',  flag: flagEmoji('PG') },
  { code: 'PH', nameEn: 'Philippines',          dialCode: '+63',   flag: flagEmoji('PH') },
  { code: 'PK', nameEn: 'Pakistan',             dialCode: '+92',   flag: flagEmoji('PK') },
  { code: 'PW', nameEn: 'Palau',                dialCode: '+680',  flag: flagEmoji('PW') },
  { code: 'SB', nameEn: 'Solomon Islands',      dialCode: '+677',  flag: flagEmoji('SB') },
  { code: 'SG', nameEn: 'Singapore',            dialCode: '+65',   flag: flagEmoji('SG') },
  { code: 'TH', nameEn: 'Thailand',             dialCode: '+66',   flag: flagEmoji('TH') },
  { code: 'TJ', nameEn: 'Tajikistan',           dialCode: '+992',  flag: flagEmoji('TJ') },
  { code: 'TL', nameEn: 'Timor-Leste',          dialCode: '+670',  flag: flagEmoji('TL') },
  { code: 'TM', nameEn: 'Turkmenistan',         dialCode: '+993',  flag: flagEmoji('TM') },
  { code: 'TO', nameEn: 'Tonga',                dialCode: '+676',  flag: flagEmoji('TO') },
  { code: 'TV', nameEn: 'Tuvalu',               dialCode: '+688',  flag: flagEmoji('TV') },
  { code: 'TW', nameEn: 'Taiwan',               dialCode: '+886',  flag: flagEmoji('TW') },
  { code: 'UZ', nameEn: 'Uzbekistan',           dialCode: '+998',  flag: flagEmoji('UZ') },
  { code: 'VN', nameEn: 'Vietnam',              dialCode: '+84',   flag: flagEmoji('VN') },
  { code: 'VU', nameEn: 'Vanuatu',              dialCode: '+678',  flag: flagEmoji('VU') },
  { code: 'WF', nameEn: 'Wallis and Futuna',    dialCode: '+681',  flag: flagEmoji('WF') },
  { code: 'WS', nameEn: 'Samoa',                dialCode: '+685',  flag: flagEmoji('WS') },
];

/** Look up a country by ISO code */
export function getCountry(code: string): Country | undefined {
  return COUNTRIES.find((c) => c.code === code);
}

 
export function getCountryName(
  code?: string | null,
  translate?: (key: string, fallback: string) => string,
): string {
  if (!code) {
    return '';
  }
  const country = getCountry(code.toUpperCase());
  if (!country) {
    return code.toUpperCase();
  }
  return translate ? translate(`country.${country.code}`, country.nameEn) : country.nameEn;
}

/** Get dial code for a country code */
export function getDialCode(countryCode: string): string {
  return getCountry(countryCode)?.dialCode ?? '';
}

/** EU member states — used by the tenant invitation flow to decide whether
 *  the synchronous VIES VAT check is expected (the backend handles the
 *  actual call; the frontend just shows a hint). */
export const EU_MEMBER_CODES: ReadonlySet<string> = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
  'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
]);

export function isEuCountry(code: string | null | undefined): boolean {
  if (!code) return false;
  return EU_MEMBER_CODES.has(code.toUpperCase());
}
